import { Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import AddButton from './AddButton';
import CancelButton from './CancelButton';
import Datatype from './Datatype';
import PrimaryButton from './PrimaryButton';
import { globalStyles as styles } from './style/gstheme';
import TitledTextField from './TitledTextField';
import { DATATYPE_LIMITS, DEFAULT_LANG, MAX_LITERAL_LENGTH } from './utils/constants';
import { checkValidity, sanitiseText } from './utils/utils';

class GetDatatypeValueDialog extends React.Component {

    state = {
        open: false,
        literal: "",
        needsValidating: false,
        existingValues: [],
        existing: false,
        pattern: null,
        maxLength: null,
        isValidPattern: true,
        error: false
    };

    // Need to check if an incoming revision causes a duplicate
    static getDerivedStateFromProps(props, state) {
        if (state.pattern !== props.pattern || state.maxLength !== props.maxLength) {    
            return { needsValidating: true, existingValues: props.existingValues, pattern: props.pattern, maxLength: props.maxLength };
        }
        if (state.literal && state.literal !== "" && props.existingValues && props.existingValues.length > 0) {
            if (props.existingValues.length === state.existingValues.length) {
                const hasChanged = props.existingValues.some(ev => !state.existingValues.includes(ev));
                if (!hasChanged) return null;
            }
            return { needsValidating: true, existingValues: props.existingValues };
        }
        return null;
    }

    componentDidUpdate() {
        if (this.state.needsValidating && this.state.literal && this.state.literal !== "") this.checkValid();
    }

    handleClickOpen = () => { this.setState({ open: true }); };

    addLiteral = () => {
        this.setState({ open: false });
        this.props.onAddLiteral(this.state.literal, this.props.datatype);
        this.setState({ literal: "" });
    };

    exitAddLiteral = () => this.setState({ open: false, literal: "", error: false });

    checkValid() {
        const { datatype, existingValues } = this.props;
        const text = this.state.literal.trim();
        if (text === "") {
            this.setState({ error: false, existing: false, needsValidating: false });
            return;
        }
        let validity = checkValidity(text, datatype, existingValues, this.props.maxLength);
        let isValidPattern = true;
        if (validity.isValid && this.props.pattern) {
            try {
                const re = new RegExp(this.props.pattern);
                isValidPattern = re.test(text);
                validity.isValid = isValidPattern;
            } catch (e) {
                validity.isValid = false;
            }
        }
        this.setState({ error: !validity.isValid, isValidPattern, existing: validity.existing, needsValidating: false });
    }

    handleChange = (event) => {
        const t = event.target;
        const cleanText = sanitiseText(t.value.trimLeft());
        this.setState({ [t.name]: cleanText }, this.checkValid);
    };

    render() {

        const { literal, error, existing, isValidPattern } = this.state;
        const { classes, datatype, title, name, placeholder, pattern } = this.props;
        const datatypeLimits = DATATYPE_LIMITS[datatype];

        return (
            <React.Fragment>
                <AddButton dataTest="add-value" title="Add a Value" onClick={this.handleClickOpen} />
                <Dialog
                    data-test="createliteral-dialog"
                    className={classes.dialog}
                    open={this.state.open}
                    onClose={this.exitAddLiteral}
                    aria-labelledby="createliteral-dialog-title">
                    <DialogTitle disableTypography className={classes.mainHeading} id="createliteral-dialog-title">{title || "Add Value"}</DialogTitle>
                    <DialogContent style={{ height: '140px', width: '500px' }}>
                        <form autoComplete="off" noValidate="novalidate">
                            {pattern && <Typography data-test="pattern-expected" variant="caption">This property expects text matching the pattern: {pattern}</Typography>}
                            <TitledTextField
                                dataTest="datatype-value"
                                name="literal"
                                title={name || "Value"}
                                autoFocus={true}
                                error={error || existing}
                                errorText={existing ?
                                    "This value already exists."
                                    :
                                    (
                                        error && datatype !== "" && !(this.props.maxLength && literal.length > this.props.maxLength) ?
                                            isValidPattern ?
                                                "The value is not valid for the datatype."
                                                :
                                                'The text must match: ' + pattern
                                            :
                                            null
                                    )}
                                required={true}
                                helperText={
                                    error && datatype !== "" && datatypeLimits && datatypeLimits.minimumValue ?
                                        "The supported range is from " + datatypeLimits.minimumValue + " up to " + datatypeLimits.maximumValue
                                        : null
                                }
                                startAdornment={
                                    datatype ?
                                    <Datatype datatype={datatype} lang={DEFAULT_LANG}/>
                                        :
                                         null

                                }
                                placeholder={placeholder}
                                text={literal}
                                maxLength={this.props.maxLength ?
                                    this.props.maxLength
                                    :
                                    datatypeLimits && datatypeLimits.maxLength ? datatypeLimits.maxLength : MAX_LITERAL_LENGTH}
                                onEnter={!error && literal.length > 0 ? this.addLiteral : null}
                                onChange={this.handleChange} />
                        </form>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <CancelButton onClick={this.exitAddLiteral} />
                        <PrimaryButton disabled={literal.trim().length === 0 || error || existing} onClick={this.addLiteral}>Add</PrimaryButton>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

GetDatatypeValueDialog.defaultProps = {
    existingValues: [],
    pattern: null,
    maxLength: null
};

GetDatatypeValueDialog.propTypes = {
    datatype: PropTypes.string.isRequired,
    existingValues: PropTypes.array,
    placeholder: PropTypes.string,
    maxLength: PropTypes.number,
    pattern: PropTypes.string,
    title: PropTypes.string,
    name: PropTypes.string,
    onAddLiteral: PropTypes.func.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(GetDatatypeValueDialog);
